/**
 * SubMenu Store - Booku V2
 *
 * Store Zustand untuk state management SubMenuBar.
 * SubMenuBar adalah bar horizontal navigasi di bawah AppTabBar
 * yang muncul saat user klik menu utama yang punya children.
 *
 * Alur navigasi:
 * 1. User klik "Buku Pengawasan" di sidebar
 * 2. SubMenuBar muncul dengan children level 1
 * 3. User klik "Hutang" (branch item)
 * 4. SubMenuBar berubah menampilkan children Hutang + tombol Back
 * 5. User klik "Hutang Usaha" (leaf item)
 * 6. SubMenuBar ditutup, tab "Hutang Usaha" dibuka
 */

import { create } from 'zustand';
import type { MenuConfigItem } from '@/config/menuConfig';

// =============================================================================
// TYPES
// =============================================================================

/** Path segment untuk breadcrumb navigation */
export interface SubMenuPath {
  id: string;
  label: string;
  /** Items yang tersedia di level ini (untuk navigasi back) */
  items: MenuConfigItem[];
}

interface SubMenuState {
  /** Apakah SubMenuBar sedang terbuka */
  isOpen: boolean;

  /** ID menu utama yang aktif (untuk highlight di sidebar) */
  activeMainMenuId: string | null;

  /** Path navigasi untuk breadcrumb (history navigation) */
  path: SubMenuPath[];

  /** Items yang sedang ditampilkan di SubMenuBar */
  currentItems: MenuConfigItem[];

  // ===========================================================================
  // ACTIONS
  // ===========================================================================

  /**
   * Buka SubMenuBar dari menu utama di sidebar.
   * Reset semua state dan mulai dari level 1.
   *
   * @param mainMenuId - ID menu utama (untuk highlight sidebar)
   * @param label - Label untuk breadcrumb awal
   * @param items - Items level 1 dari menu utama
   */
  openSubMenu: (mainMenuId: string, label: string, items: MenuConfigItem[]) => void;

  /**
   * Navigasi ke sub-level (child branch).
   * Dipanggil saat user klik item branch di SubMenuBar.
   *
   * @param branchId - ID branch item yang diklik
   * @param label - Label untuk breadcrumb
   * @param items - Children dari branch item
   */
  navigateToChild: (branchId: string, label: string, items: MenuConfigItem[]) => void;

  /**
   * Kembali ke level sebelumnya.
   * Dipanggil saat user klik tombol Back.
   */
  navigateBack: () => void;

  /**
   * Tutup SubMenuBar.
   * Dipanggil saat user klik item leaf, overlay, atau tombol close.
   */
  closeSubMenu: () => void;

  /**
   * Reset state ke default (dipanggil saat logout).
   */
  reset: () => void;
}

// =============================================================================
// DEFAULT STATE
// =============================================================================

const defaultState = {
  isOpen: false,
  activeMainMenuId: null,
  path: [] as SubMenuPath[],
  currentItems: [] as MenuConfigItem[],
};

// =============================================================================
// STORE
// =============================================================================

export const useSubMenuStore = create<SubMenuState>()((set) => ({
  ...defaultState,

  openSubMenu: (mainMenuId, label, items) =>
    set({
      isOpen: true,
      activeMainMenuId: mainMenuId,
      // Mulai dengan path kosong, items level 1 ditampilkan
      path: [{ id: mainMenuId, label, items }],
      currentItems: items,
    }),

  navigateToChild: (branchId, label, items) =>
    set((state) => ({
      // Push ke path untuk history
      path: [...state.path, { id: branchId, label, items }],
      currentItems: items,
    })),

  navigateBack: () =>
    set((state) => {
      // Tidak bisa back jika sudah di root
      if (state.path.length <= 1) {
        return state;
      }

      // Pop path terakhir
      const newPath = state.path.slice(0, -1);
      const parentLevel = newPath[newPath.length - 1];

      return {
        path: newPath,
        currentItems: parentLevel.items,
      };
    }),

  closeSubMenu: () =>
    set({
      isOpen: false,
      // Tidak reset activeMainMenuId agar sidebar masih highlight
      // saat user baru saja menutup SubMenuBar
      path: [],
      currentItems: [],
    }),

  reset: () => set(defaultState),
}));

// =============================================================================
// HELPER HOOKS
// =============================================================================

/**
 * Hook untuk mendapatkan status apakah SubMenuBar sedang terbuka
 */
export function useSubMenuOpen(): boolean {
  return useSubMenuStore((state) => state.isOpen);
}

/**
 * Hook untuk mendapatkan ID menu utama yang aktif di SubMenuBar
 */
export function useActiveMainMenuId(): string | null {
  return useSubMenuStore((state) => state.activeMainMenuId);
}

/**
 * Hook untuk mendapatkan breadcrumb path
 */
export function useSubMenuPath(): SubMenuPath[] {
  return useSubMenuStore((state) => state.path);
}

/**
 * Hook untuk mendapatkan items yang sedang ditampilkan
 */
export function useSubMenuItems(): MenuConfigItem[] {
  return useSubMenuStore((state) => state.currentItems);
}

/**
 * Hook untuk mendapatkan label breadcrumb (untuk display)
 * Format: "Buku Pengawasan > Hutang"
 */
export function useSubMenuBreadcrumb(): string {
  const path = useSubMenuStore((state) => state.path);
  return path.map((p) => p.label).join(' > ');
}

/**
 * Hook untuk cek apakah bisa navigasi back
 */
export function useCanNavigateBack(): boolean {
  return useSubMenuStore((state) => state.path.length > 1);
}
